import {Chart, type LegendItem} from 'chart.js';

type LegendLabelsGenerator = (chart: Chart) => LegendItem[];

type LegendOverrides = {
  plugins?: {
    legend?: {
      labels?: {
        generateLabels?: LegendLabelsGenerator;
      };
    };
  };
};

/**
 * Returns the `generateLabels` implementation Chart.js itself would use for the given chart.
 *
 * Chart.js resolves this per chart type: line/bar keep the dataset-based generator on
 * `Chart.defaults.plugins.legend` (one legend entry per dataset, labelled `dataset.label`), while
 * doughnut/pie replace it through `Chart.overrides[type]` with a per-slice generator (one entry per
 * `data.labels[i]`, carrying the `index` that the doughnut legend's own `onClick` toggles).
 *
 * Anything that wants to decorate the generated legend items must start from the *type's* generator
 * rather than always from `Chart.defaults`: feeding a doughnut the dataset-based generator collapses
 * its legend into a single entry reading "undefined" (a doughnut dataset carries no `label`) and
 * breaks click-to-toggle, because the item then carries `datasetIndex` instead of `index`.
 */
export function resolveDefaultGenerateLabels(chart: Chart): LegendLabelsGenerator {
  // Chart.js's runtime `Config` wrapper exposes the chart type, but its public typing only covers
  // the user-supplied configuration object, which may declare the type per dataset instead.
  const chartType = (chart.config as unknown as {type: string}).type;
  const overrides = (Chart.overrides as unknown as Record<string, LegendOverrides | undefined>)[chartType];
  const typeGenerator = overrides?.plugins?.legend?.labels?.generateLabels;
  if (typeGenerator) return typeGenerator;
  return (Chart.defaults.plugins as unknown as {
    legend: {labels: {generateLabels: LegendLabelsGenerator}};
  }).legend.labels.generateLabels;
}
